import { AlertCircle, LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import type { InstrumentDto, InstrumentKind } from '../../shared/types';
import { api } from '../api';
import { kindLabel } from '../format';
import type { InstrumentCreateInput } from '../review-form';

const defaults: InstrumentCreateInput = {
  symbol: '', name: '', kind: 'US_EQUITY', market: 'NASDAQ', quoteCurrency: 'USD',
  unit: 'share', providerSymbol: '', coingeckoId: '',
};

function defaultsForKind(kind: InstrumentKind) {
  if (kind === 'TH_EQUITY') return { market: 'SET', quoteCurrency: 'THB' as const, unit: 'share' };
  if (kind === 'CRYPTO') return { market: 'CRYPTO', quoteCurrency: 'USD' as const, unit: 'coin' };
  if (kind === 'GOLD') return { market: 'METAL', quoteCurrency: 'USD' as const, unit: 'oz' };
  return { market: 'NASDAQ', quoteCurrency: 'USD' as const, unit: 'share' };
}

export function InstrumentForm({
  initial,
  cancel,
  saved,
}: {
  initial?: Partial<InstrumentCreateInput>;
  cancel: () => void;
  saved: (instrument: InstrumentDto) => Promise<void> | void;
}) {
  const [form, setForm] = useState<InstrumentCreateInput>({ ...defaults, ...initial });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const instrument = await api<InstrumentDto>('/instruments', { method: 'POST', body: JSON.stringify(form) });
      await saved(instrument);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'เพิ่มสินทรัพย์ไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  return <form className="instrument-form form-grid" onSubmit={submit}>
    <label className="field"><span>Symbol<b>*</b></span><input required maxLength={24} value={form.symbol} onChange={(event) => setForm({ ...form, symbol: event.target.value.toUpperCase() })} placeholder="เช่น SCHD" /></label>
    <label className="field"><span>ชื่อสินทรัพย์<b>*</b></span><input required maxLength={120} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
    <label className="field"><span>ประเภท<b>*</b></span><select value={form.kind} onChange={(event) => { const kind = event.target.value as InstrumentKind; setForm({ ...form, kind, ...defaultsForKind(kind) }); }}>{Object.entries(kindLabel).filter(([key]) => key !== 'FIAT').map(([key, label]) => <option value={key} key={key}>{label}</option>)}</select></label>
    <label className="field"><span>ตลาด</span><input maxLength={24} value={form.market} onChange={(event) => setForm({ ...form, market: event.target.value })} placeholder="เช่น NYSE Arca" /></label>
    <label className="field"><span>สกุลเงินราคา<b>*</b></span><select value={form.quoteCurrency} onChange={(event) => setForm({ ...form, quoteCurrency: event.target.value as 'USD' | 'THB' })}><option>USD</option><option>THB</option></select></label>
    <label className="field"><span>หน่วย<b>*</b></span><input required readOnly={form.kind === 'GOLD'} maxLength={24} value={form.unit} onChange={(event) => setForm({ ...form, unit: event.target.value })} placeholder="share" /></label>
    {form.kind === 'CRYPTO'
      ? <label className="field field-wide"><span>CoinGecko ID</span><input maxLength={80} value={form.coingeckoId} onChange={(event) => setForm({ ...form, coingeckoId: event.target.value })} placeholder="เช่น bitcoin" /></label>
      : <label className="field field-wide"><span>Provider symbol</span><input maxLength={80} value={form.providerSymbol} onChange={(event) => setForm({ ...form, providerSymbol: event.target.value })} placeholder="เว้นว่างเพื่อใช้ Symbol ด้านบน" /></label>}
    {error && <div className="form-error field-wide"><AlertCircle />{error}</div>}
    <div className="instrument-form-actions"><button type="button" className="button tertiary" disabled={busy} onClick={cancel}>ยกเลิก</button><button className="button primary" disabled={busy}>{busy ? <LoaderCircle className="spin" size={17} /> : null} บันทึกสินทรัพย์</button></div>
  </form>;
}
